import { useState } from "react";
import { Cpu, Monitor, Volume2, ChevronRight, ArrowRight } from "lucide-react";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { services } from "../../data/mock";

const iconMap = {
  Cpu: Cpu,
  Monitor: Monitor,
  Volume2: Volume2,
};

export const ServicesSection = () => {
  const [activeService, setActiveService] = useState(null);

  return (
    <section id="servicios" className="relative py-28 lg:py-36 overflow-hidden">
      <div className="absolute inset-0 bg-[#080815]" />
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[radial-gradient(circle,rgba(0,119,255,0.03)_0%,transparent_65%)]" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[radial-gradient(circle,rgba(0,200,255,0.02)_0%,transparent_65%)]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div className="max-w-2xl mb-16">
          <span className="text-sm font-semibold text-[#0077FF] tracking-widest uppercase">
            Servicios
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-[2.75rem] font-bold text-white mt-4 leading-tight">
            Soluciones tecnológicas{" "}
            <span className="text-white/30">para cada necesidad</span>
          </h2>
          <p className="text-white/35 mt-5 text-lg leading-relaxed">
            Tres divisiones especializadas para cubrir todas tus necesidades
            tecnológicas, desde sistemas embebidos hasta audio profesional.
          </p>
        </div>

        {/* Service cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {services.map((service) => {
            const Icon = iconMap[service.icon];
            const isActive = activeService === service.id;

            return (
              <Card
                key={service.id}
                className={`group relative bg-white/[0.015] border-white/[0.06] hover:border-[#0077FF]/25 p-8 rounded-2xl cursor-pointer transition-all duration-500 overflow-hidden ${
                  isActive
                    ? "border-[#0077FF]/30 bg-[#0077FF]/[0.02]"
                    : ""
                }`}
                onClick={() =>
                  setActiveService(isActive ? null : service.id)
                }
              >
                {/* Subtle corner glow on hover */}
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-[radial-gradient(circle,rgba(0,119,255,0.08)_0%,transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                {/* Icon */}
                <div className="relative w-14 h-14 rounded-xl bg-[#50bcfa]/[0.08] border border-[#50bcfa]/15 flex items-center justify-center mb-6 group-hover:bg-[#50bcfa]/[0.12] transition-colors duration-500">
                  <Icon size={24} className="text-[#50bcfa]" />
                </div>

                {/* Content */}
                <Badge
                  variant="outline"
                  className="text-[#00a2ff] border-[#0077FF]/15 mb-4 text-[11px] tracking-wider font-medium"
                >
                  {service.subtitle}
                </Badge>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-white/35 text-sm leading-relaxed mb-6">
                  {service.description}
                </p>

                {/* Features - expandable */}
                <div
                  className={`space-y-3 overflow-hidden transition-all duration-500 ease-out ${
                    isActive
                      ? "max-h-[500px] opacity-100 mb-2"
                      : "max-h-0 opacity-0"
                  }`}
                >
                  {service.features.map((feature, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-3"
                    >
                      <ChevronRight
                        size={14}
                        className="text-[#50bcfa] mt-0.5 flex-shrink-0"
                      />
                      <div>
                        <p className="text-sm font-medium text-white/70">
                          {feature.name}
                        </p>
                        <p className="text-xs text-white/25 mt-0.5">
                          {feature.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Toggle indicator */}
                <div className="relative flex items-center gap-2 mt-4 text-sm text-[#0077FF] group-hover:text-[#0077FF]/80 transition-colors duration-300">
                  <span className="text-[13px]">
                    {isActive ? "Ver menos" : "Ver detalles"}
                  </span>
                  <ArrowRight
                    size={14}
                    className={`transition-transform duration-300 ${
                      isActive ? "rotate-90" : ""
                    }`}
                  />
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};
